import styled from '@emotion/styled';
import { Grid, Link, Skeleton, Typography } from '@mui/material';
import mixpanel from 'mixpanel-browser';
import { useEffect, useState } from 'react';

import { PlanGroup } from '@d-id/types';

import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { toast } from '@/components/toast';
import { useReadmeioLogin } from '@/hooks/useReadmeioLogin';
import { generateApiKey, getApiKeyMetadata } from '@/services/api';
import { useAuth } from '@/services/auth';

import Button from '@/components/Buttons/Button';
import { pricingUrl } from '@/environment';
import CodeFile from '../../../images/CodeFile.svg';
import RegenerateArrow from '../../../images/RegenerateArrow.svg';
import Card from './Card';
import CopyToClipboard from './CopyToClipboardButton';
import KnowledgeMessage from './KnowledgeMessage';
import { RegenerateApiKeyModal } from './RegeneratePopup';
import WarningMessage from './WarningMessage';

const StyledCard = styled(Card)`
    justify-content: flex-start;
`;

const Container = styled.div`
    font-size: 1.6rem;
`;

const IconContainer = styled.div`
    padding-right: 10px;
    margin-left: 60px;
`;

const APIKeyContainer = styled.div<{ noApiKey: boolean }>`
    background-color: ${({ theme }) => theme.colors.black[100]};
    display: flex;
    flex-direction: column;
    align-items: left;
    justify-content: center;
    height: 120px;
    width: 100%;
    color: ${({ noApiKey }) => (noApiKey ? '#a3a3a3' : '#242625')};
    border-radius: 8px;
    line-break: anywhere;
    padding: 10px 40px;
    font-weight: 400;
    font-size: 16px;
    user-select: text;
    margin-bottom: 14px;
`;

const ActionButton = styled(Button, { shouldForwardProp: p => p !== 'hidden' })<{
    hidden: boolean;
}>`
    padding: 0.5rem 5rem;
    border-radius: 8px;
    line-height: 1;
    width: 100%;
    height: 40px;
    min-height: 40px;

    display: ${({ hidden }) => (hidden ? 'none' : 'flex')};
`;

const Title = styled(Typography)`
    margin: 16px 0 12px;
`;

const Description = styled.p`
    margin-top: 0px;
    margin-bottom: 14px;
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
`;

const CreatedAtWrapper = styled.p<{ hidden: boolean }>`
    font-size: 1.4rem;
    color: ${({ theme }) => theme.colors.black[600]};
    display: ${({ hidden }) => (hidden ? 'none' : 'flex')};
`;

const HideableMaskedIcon = styled(MaskedIcon)<{ hidden: boolean }>`
    display: ${({ hidden }) => (hidden ? 'none' : 'flex')};
    margin-right: 15px;
`;

const ApiKeyText = styled(Typography)`
    font-size: 1.4rem;
    line-height: 2rem;
`;

const actionButtonClickedHandler = (
    generateApiKeyForUser: () => void,
    loginWithRedirect: (options: any) => void,
    isAnonymous?: boolean
) => {
    if (isAnonymous) {
        mixpanel?.track('signup', { event: 'click', isAnonymous, from: 'api' });
        loginWithRedirect({
            redirectUri: window.location.href,
        });
    } else {
        generateApiKeyForUser();
    }
};

type ApiKey = {
    token: string;
    isTrial: boolean;
};

type ApiKeyMetadata = {
    lastModified: string;
    isTrial: boolean;
};

const getApiText = (apiKey?: ApiKey, lastModifiedApiKey?: boolean) => {
    if (!!apiKey) {
        return apiKey.token;
    } else if (lastModifiedApiKey) {
        return '• '.repeat(70);
    } else {
        return 'No API key to show yet';
    }
};

const getActionButtonText = (apiKey: boolean, lastModifiedApiKey: boolean, isAnonymousUser: boolean) => {
    if (isAnonymousUser) {
        return 'Sign up';
    }

    if (!apiKey && !lastModifiedApiKey) {
        return 'Generate new key';
    }

    if (apiKey) {
        return 'Copy to clipboard';
    }

    if (lastModifiedApiKey) {
        return 'Regenerate key';
    }
};

const PlanGroupWithSubscription = [PlanGroup.BUILD, PlanGroup.LAUNCH, PlanGroup.SCALE, PlanGroup.ENTERPRISE, PlanGroup.ENTERPRISE_TRIAL];
const SubscriptionMessage = 'Using the API? consider using one of our API subscription plans and save on credits.';

export default function ApiKey({ flex, withoutBorder = false }: { flex: number; withoutBorder?: boolean }) {
    const { user, isAnonymous, loginWithRedirect, getAccessTokenSilently } = useAuth();
    const isTrialOrFreeUser = user?.planGroup === PlanGroup.TRIAL || user?.planGroup === PlanGroup.FREE;
    const [warningMessage, setWarningMessage] = useState('');
    const [showSubscriptionMessage] = useState(!(user?.planGroup && PlanGroupWithSubscription.includes(user?.planGroup)));
    const [fetchingApiKey, setFetchingApiKey] = useState(false);
    const [apiKey, setApiKey] = useState<ApiKey>();
    const [apiKeyMetadata, setApiKeyMetadata] = useState<ApiKeyMetadata>();
    const [openRegeneratePopup, setOpenRegeneratePopup] = useState(false);
    const apiKeyText = getApiText(apiKey, !!apiKeyMetadata);
    const ActionButtonText = getActionButtonText(!!apiKey, !!apiKeyMetadata, !!isAnonymous);
    const getReadmeUrl = useReadmeioLogin();

    useEffect(() => {
        async function getApiKey() {
            setFetchingApiKey(true);
            const res = await getApiKeyMetadata();
            if (res) {
                const lastModified = new Date(res.modified_at).toLocaleDateString('en-US', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                });
                setApiKeyMetadata({
                    lastModified,
                    isTrial: !!res.is_trial,
                });
            }
            setFetchingApiKey(false);
        }

        getApiKey();
    }, []);

    useEffect(() => {
        if (apiKey) {
            setWarningMessage(`Please copy your API key now. We don't store it.`);
            return;
        }

        if (apiKeyMetadata?.isTrial || (!apiKeyMetadata && isTrialOrFreeUser)) {
            setWarningMessage(`A trial API key is restricted and has a lower priority. We'll upgrade it automatically once you subscribe.`);
            return;
        }

        setWarningMessage('');
    }, [apiKey, apiKeyMetadata, isTrialOrFreeUser]);

    const generateApiKeyForUser = async () => {
        try {
            setFetchingApiKey(true);
            const data = await generateApiKey();
            mixpanel?.track('api-key', { event: 'success' });
            const token = `${data.access_key_username}:${data.access_key_password}`;
            setApiKey({ token, isTrial: !!data.is_trial });
            toast.success({
                title: 'New API Key Created. Copy it now',
                toastId: 'api-key-generated',
            });
            await getAccessTokenSilently(true);
            setFetchingApiKey(false);
        } catch (e) {
            toast.error({ title: 'Failed to generate API key', toastId: 'api-key-error' });
            mixpanel?.track('api-key', { event: 'error' });
        }
    };

    const redirectToDoc = async () => {
        const url = await getReadmeUrl();

        if (url) {
            window.open(url);
        }
    };

    const onRegenerate = async () => {
        await generateApiKeyForUser();
        setOpenRegeneratePopup(false);
    };

    const onActionButtonClicked = () => {
        return !apiKey && !!apiKeyMetadata
            ? setOpenRegeneratePopup(true)
            : actionButtonClickedHandler(generateApiKeyForUser, loginWithRedirect, !!isAnonymous);
    };

    const HoverableLink = styled(Link)`
        color: ${({ theme }) => theme.colors.secondary.main};
        font-weight: 600;
        text-decoration: none;

        &:hover {
            cursor: pointer;
            color: ${({ theme }) => theme.colors.secondary.main};
            text-decoration: underline;
            text-decoration-color: ${({ theme }) => theme.colors.secondary.main};
        }
    `;

    const onSubscriptionLearnMore = () => {
        mixpanel?.track('go-to-api-pricing', { event: 'click', from: 'account-settings' });
        window.open(pricingUrl, '_blank');
    };

    const renderApiKey = () =>
        fetchingApiKey ? (
            <>
                <Skeleton component="div" variant="rectangular" sx={{ marginBottom: '6px' }} />
                <Skeleton component="div" variant="rectangular" sx={{ marginBottom: '6px' }} />
                <Skeleton component="div" variant="rectangular" sx={{ marginBottom: '6px' }} />
                <Skeleton component="div" variant="rectangular" sx={{ marginBottom: '6px' }} width="50%" />
                <Skeleton component="div" variant="rectangular" width="70%" />
            </>
        ) : (
            <Grid container direction="column">
                <Grid container direction="row">
                    <IconContainer hidden={!!apiKey || !!apiKeyMetadata}>
                        <MaskedIcon src={CodeFile} size={'24px'} />
                    </IconContainer>
                    <ApiKeyText variant="h5">{apiKeyText}</ApiKeyText>
                </Grid>
                <CreatedAtWrapper hidden={!apiKeyMetadata || !!apiKey}>{`Created at: ${apiKeyMetadata?.lastModified}`}</CreatedAtWrapper>
            </Grid>
        );

    return (
        <StyledCard flex={flex} gap={0} bg="white" noBorder={withoutBorder}>
            <Title variant="h3">API Key</Title>
            <Container>
                <Description>
                    <span>Create videos at scale, learn more by reading our </span>
                    <HoverableLink onClick={redirectToDoc}>API documentation</HoverableLink>
                </Description>
            </Container>
            <APIKeyContainer noApiKey={!apiKey && !apiKeyMetadata}>{renderApiKey()}</APIKeyContainer>
            <WarningMessage hidden={!warningMessage}>{warningMessage}</WarningMessage>
            <KnowledgeMessage hidden={!showSubscriptionMessage} onLearnMore={onSubscriptionLearnMore}>
                {SubscriptionMessage}
            </KnowledgeMessage>
            <ActionButton data-testid="api-key-action-button" color="secondary" onClick={onActionButtonClicked} hidden={!!apiKey}>
                <HideableMaskedIcon src={RegenerateArrow} size={'24px'} hidden={!apiKeyMetadata} />
                {ActionButtonText}
            </ActionButton>
            <CopyToClipboard text={apiKey?.token ?? ''} hidden={!apiKey} />
            <RegenerateApiKeyModal open={openRegeneratePopup} onClose={() => setOpenRegeneratePopup(false)} onRegenerate={onRegenerate} />
        </StyledCard>
    );
}
